// Signing in to a server, wherever it starts: a service's page, Connected's row, Add an MCP server, or a widget
// whose connection signed out. Each shows the same states while the user signs in (the window that opened, a
// pop-up blocker in the way with a button past it, a window closed early) and never ends in an error toast.

import { useState } from "react";
import { ExternalLink, LogIn } from "lucide-react";
import type { Connection } from "../../../shared/types";
import { possessive } from "../../../shared/ours";
import { cx } from "../../lib/util";
import { SIGN_IN_CANCELLED, type SignInOpts, type SignInWait } from "../../state/connections";

export interface SignIn {
  /** the sign-in window is open (or was blocked), and the board waits for it */
  wait: SignInWait | null;
  /** the window closed before the board heard back; waiting goes on */
  closed: boolean;
  busy: boolean;
  error: string | null;
  /** start it, on a click: go is connectServer or signInAgain, given the options that report back here */
  run: (go: (opts: SignInOpts) => Promise<Connection>) => Promise<Connection | null>;
  /** the window again, or past a blocker; false when the browser blocked it once more */
  reopen: () => boolean;
}

export function useSignIn(): SignIn {
  const [wait, setWait] = useState<SignInWait | null>(null);
  const [closed, setClosed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (go: (opts: SignInOpts) => Promise<Connection>) => {
    setBusy(true);
    setError(null);
    setClosed(false);
    try {
      return await go({ onWait: setWait, onClosed: () => setClosed(true) });
    } catch (e) {
      if ((e as Error).message !== SIGN_IN_CANCELLED) setError((e as Error).message);
      return null;
    } finally {
      setBusy(false);
      setWait(null);
    }
  };
  const reopen = () => {
    if (!wait) return false;
    if (wait.reopen()) {
      setClosed(false);
      if (wait.blocked) setWait({ ...wait, blocked: false });
      setError(null);
      return true;
    }
    if (wait.blocked) setError("Your browser blocked it again. Allow pop-ups for this site, then try once more.");
    else setWait({ ...wait, blocked: true });
    return false;
  };
  return { wait, closed, busy, error, run, reopen };
}

/**
 * What the page shows while someone signs in to name: compact under a row in a list, else a panel with room for
 * how the sign-in goes (children).
 */
export function SignInWaiting({ s, name, compact, children }: { s: SignIn; name: string; compact?: boolean; children?: React.ReactNode }) {
  if (!s.wait) return null;
  const blocked = s.wait.blocked;
  return (
    <div className={cx("hub-wait", compact && "is-compact")} role="status">
      {blocked ? (
        <>
          <b className="hub-wait-title">Your browser held back {possessive(name)} sign-in window</b>
          <p className="hub-note">Open it here. Nothing else changes.</p>
        </>
      ) : (
        <>
          <b className="hub-wait-title">
            <span className="spinner" /> Waiting for you to sign in to {name}
          </b>
          <p className="hub-note">{s.closed ? "The sign-in window closed. If you finished, this updates in a moment." : "Finish in the window that opened. This updates by itself."}</p>
        </>
      )}
      {children}
      <div className="inline-actions">
        {blocked ? (
          <button className="btn btn-sm btn-primary" onClick={() => s.reopen()}>
            <LogIn size={13} /> Open {possessive(name)} sign-in
          </button>
        ) : (
          <button className="btn btn-sm" onClick={() => s.reopen()}>
            <ExternalLink size={13} /> {s.closed ? "Open the sign-in again" : "Show the sign-in window"}
          </button>
        )}
        <button className="btn btn-sm btn-ghost" onClick={() => s.wait?.cancel()}>
          Cancel
        </button>
      </div>
      {s.error && <div className="callout is-bad">{s.error}</div>}
    </div>
  );
}
